import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search } from "lucide-react";

import { getArchivedInventory, unarchiveInventory } from "src/api/inventory";
import useDebounce from "src/hooks/useDebounce";
import CustomModal from "src/components/CustomModal";
import CustomConfirmDialog from "src/components/CustomConfirmDialog";
import { DataTable, type Column, type RowsPerPageOption } from "src/components/common";
import { renderCurrency, renderNumber, showSuccessToast } from "src/lib/utils";
import type { ProductRow } from "src/types/inventory";

interface ArchivedInventoryModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	productLabel: string;
}

/** Archived products as a modal (decision D2). `/inventory/archived` redirects here via `?archived=1`. */
const ArchivedInventoryModal = ({ open, onOpenChange, productLabel }: ArchivedInventoryModalProps) => {
	const queryClient = useQueryClient();
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState<RowsPerPageOption>(10);

	const handleSearch = useDebounce((term: string) => {
		setSearch(term);
		setPage(1);
	}, 500);

	const { data, isLoading } = useQuery({
		queryKey: ["inventory", "archived", page, limit, search],
		queryFn: () => getArchivedInventory({ page, limit: limit === "All" ? 9999 : limit, search }),
		enabled: open,
	});

	const { mutate: restoreProduct } = useMutation(unarchiveInventory, {
		onSuccess: ({ message }) => {
			showSuccessToast(message || `${productLabel} restored`);
			queryClient.invalidateQueries(["inventory"]);
		},
	});

	const list = data?.data;
	const products = list?.data ?? [];

	const columns: Column<ProductRow>[] = [
		{ key: "code", header: "Item Code", cell: (row) => row.item_id ?? "—" },
		{ key: "name", header: "Product", primary: true, cell: (row) => row.name ?? "—" },
		{ key: "sku", header: "SKU", cell: (row) => row.sku ?? "—" },
		{ key: "qty", header: "Qty", cell: (row) => renderNumber(row.available_qty ?? 0) },
		{ key: "price", header: "Selling Price", cell: (row) => renderCurrency(row.price ?? 0) },
		{
			key: "actions",
			header: "",
			hideOnMobile: true,
			className: "tw-w-[110px] tw-text-right",
			cell: (row) => (
				<CustomConfirmDialog
					trigger={
						<button
							type="button"
							className="tw-rounded-lg tw-px-2 tw-py-1 tw-text-[12.5px] tw-font-semibold tw-text-teal hover:tw-bg-teal-tint"
						>
							Restore
						</button>
					}
					mutateFn={() => restoreProduct({ uuid: row.uuid })}
				/>
			),
		},
	];

	return (
		<CustomModal open={open} onOpenChange={onOpenChange} size="lg" title={`Archived ${productLabel}`}>
			<div className="tw-space-y-4">
				<div className="tw-relative tw-w-full sm:tw-w-[280px]">
					<Search className="tw-pointer-events-none tw-absolute tw-left-3 tw-top-1/2 tw--translate-y-1/2 tw-h-4 tw-w-4 tw-text-text-soft" />
					<input
						type="search"
						placeholder="Search archived products..."
						onChange={(event) => handleSearch(event.target.value.trim())}
						className="tw-h-10 tw-w-full tw-rounded-[10px] tw-border tw-border-border tw-bg-white tw-pl-9 tw-pr-3 tw-text-[13.5px] tw-text-text placeholder:tw-text-text-soft focus:tw-border-teal focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-teal/20"
					/>
				</div>

				<DataTable
					data={products}
					columns={columns}
					rowKey={(row) => row.uuid}
					loading={isLoading}
					totalRows={list?.total ?? products.length}
					page={page}
					onPageChange={setPage}
					rowsPerPage={limit}
					onRowsPerPageChange={setLimit}
					emptyTitle="No archived products"
					emptyDescription="Products you archive will be listed here and can be restored."
				/>
			</div>
		</CustomModal>
	);
};

export default ArchivedInventoryModal;
